import { computeFocusTarget } from '@Hooks/useDeleteFocusManager';
import { findParentNode, flattenNodeTree } from './queryTreeUtils';

const SIDE_PANEL_FOCUS_TARGET = 'querybuilder-tabs';
const NO_FOCUS_RING_CLASS = 's-els-no-focus';

export function allowFocusRing() {
  document.body?.classList?.remove(NO_FOCUS_RING_CLASS);
}

function focusDragButtonByUuid(targetUuid) {
  if (!targetUuid) {
    return false;
  }

  const targetContainer = document.querySelector(`[data-uuid="${targetUuid}"]`);
  const targetButton = targetContainer?.querySelector('[data-e2e="drag-button"]');

  if (!targetButton || typeof targetButton.focus !== 'function') {
    return false;
  }

  targetButton.focus();
  return document.activeElement === targetButton;
}

function focusSidePanelTabs() {
  const sidePanel = document.getElementById(SIDE_PANEL_FOCUS_TARGET);
  const firstFocusable = sidePanel?.querySelector('a, button, input, [tabindex]:not([tabindex="-1"])');
  firstFocusable?.focus();
}

/**
 * Focus the first of `targetUuids` whose drag button is still in the DOM, falling back to the
 * side panel when none of them are.
 */
function scheduleFocus(targetUuids) {
  const candidates = (Array.isArray(targetUuids) ? targetUuids : [targetUuids]).filter(Boolean);

  // Delete/ungroup are reached by activating a button, which a screen reader does with a click.
  allowFocusRing();

  const focusByTarget = () => {
    if (candidates.some((uuid) => focusDragButtonByUuid(uuid))) {
      return;
    }

    focusSidePanelTabs();
  };

  requestAnimationFrame(() => {
    requestAnimationFrame(focusByTarget);
  });
}

function resolveNextFocusUuid(itemUuids, referenceUuids, deletingUuid) {
  const [firstItemUuid] = itemUuids;

  if (!firstItemUuid) {
    return null;
  }

  if (!deletingUuid) {
    return firstItemUuid;
  }

  const computedTarget = computeFocusTarget(referenceUuids, deletingUuid, SIDE_PANEL_FOCUS_TARGET);
  if (computedTarget !== SIDE_PANEL_FOCUS_TARGET && itemUuids.includes(computedTarget)) {
    return computedTarget;
  }

  return firstItemUuid;
}

function resolveListFocusUuid(nodesList, deletingUuid, previousNodesList) {
  const currentList = Array.isArray(nodesList) ? nodesList : [];
  const referenceList = Array.isArray(previousNodesList) ? previousNodesList : currentList;
  // Flattened, because a deleted querylet is usually nested inside a group. Comparing against
  // top-level uuids only would make computeFocusTarget miss every grouped node and drop focus
  // onto the first querylet in the container.
  const itemUuids = flattenNodeTree(currentList)
    .map((node) => node?.uuid)
    .filter(Boolean);
  const referenceUuids = flattenNodeTree(referenceList)
    .map((node) => node?.uuid)
    .filter(Boolean);

  // Keep focus in node list whenever possible; only fallback to side panel when no nodes remain.
  return resolveNextFocusUuid(itemUuids, referenceUuids, deletingUuid);
}

function resolveParentScopedFocusUuids(nodesList, previousNodesList, deletingUuid) {
  const previousParent = findParentNode(previousNodesList, deletingUuid);
  if (!previousParent) {
    return [];
  }

  const currentUuids = new Set(
    flattenNodeTree(nodesList)
      .map((node) => node?.uuid)
      .filter(Boolean)
  );
  const survivingDescendantNodes = flattenNodeTree(previousParent.items).filter(
    (node) => node?.uuid && node.uuid !== deletingUuid && currentUuids.has(node.uuid)
  );
  const survivingQueryletUuids = survivingDescendantNodes.filter((node) => node.id !== 'wrapper').map((node) => node.uuid);
  const survivingGroupUuids = survivingDescendantNodes.filter((node) => node.id === 'wrapper').map((node) => node.uuid);

  // Prefer the first surviving querylet in the parent group, then nested group
  // controls, and only finally the parent group itself.
  return [...survivingQueryletUuids, ...survivingGroupUuids, previousParent.uuid].filter(Boolean);
}

function isFocusTargetInParentScope(targetUuid, previousNodesList, deletingUuid) {
  if (!targetUuid) {
    return false;
  }

  const previousParent = findParentNode(previousNodesList, deletingUuid);
  if (!previousParent) {
    return true;
  }

  return previousParent.uuid === targetUuid || flattenNodeTree(previousParent.items).some((node) => node?.uuid === targetUuid);
}

export function focusNextDragButtonOrSidePanel(nodesList, deletingUuid, previousNodesList = nodesList) {
  scheduleFocus(resolveListFocusUuid(nodesList, deletingUuid, previousNodesList));
}

/**
 * Focus the node nearest the mutation point, falling back to the surviving node tree before
 * giving up to the side panel. `preferredUuid` may point at a node the update removed (e.g. a
 * wrapper that collapsed once it was down to a single item), so it is only a first guess.
 */
export function focusNodeOrNextInList({ preferredUuid, nodesList, mutatedUuid, previousNodesList = nodesList }) {
  const scopedFocusUuids = resolveParentScopedFocusUuids(nodesList, previousNodesList, mutatedUuid);
  const scopedPreferredUuid = isFocusTargetInParentScope(preferredUuid, previousNodesList, mutatedUuid) ? preferredUuid : null;

  scheduleFocus([scopedPreferredUuid, ...scopedFocusUuids, resolveListFocusUuid(nodesList, mutatedUuid, previousNodesList)]);
}
